import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { AuthService } from '../../../../../core/services/auth/auth.service';
import { DecimalFormatPipe } from '../../../../../shared';
import { DRP } from '../../drp';
import { ABRIR_DETALHADO, LinhaDRP, LINHAS_DRP } from '../../drp-linhas';

interface Config {
    /** Se pode visualizar as informações de Qualidade de Valor (QV) */
    canShowQV: boolean;
    /** Se pode visualizar as informações de D.R.P */
    canShowDRP: boolean;
    /** Se pode visualizar as informações de D.R.P Custos */
    canShowDRPCustos: boolean;

    /** Se pode visualizar a coluna de percentual do D.R.P */
    canShowColPerc?: boolean;
    /** Se pode visualizar a coluna de percentual de composição do D.R.P */
    canShowColPercComposicao?: boolean;
}

@Component({
    selector: 'sgt-pedido-drp-view',
    templateUrl: './pedido-drp-view.component.html',
    styleUrls: ['./pedido-drp-view.component.less']
})
export class PedidoDRPViewComponent implements OnInit, OnChanges {
    config: Config = {
        canShowQV: false,
        canShowDRP: false,
        canShowDRPCustos: false
    };
    /** Linhas do D.R.P visíveis no modo e nas permissões atuais */
    linhas: LinhaDRP[] = [];

    @Input() drp: DRP;
    /** Se a tabela mostra os componentes do D.R.P ou apenas as etapas da cascata */
    @Input() detalhado = ABRIR_DETALHADO;

    constructor(private authService: AuthService,
                private decimalFormatPipe: DecimalFormatPipe) {}

    ngOnInit(): void {
        this.config = {
            canShowQV: this.authService.podeVisualizarQV(),
            canShowDRP: this.authService.podeVisualizarDRP(),
            canShowDRPCustos: this.authService.podeVisualizarDRPCustos()
        };

        // Somente se puder visualizar o D.R.P é possível visualizar as colunas de percentual do D.R.P e percentual de composição do D.R.P
        this.config.canShowColPerc = this.config.canShowDRP;
        this.config.canShowColPercComposicao = this.config.canShowDRP;

        this.montarLinhas();
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.detalhado && !changes.detalhado.firstChange) {
            this.montarLinhas();
        }
    }

    /** Participação da linha sobre o valor total líquido, formatada, ou vazio quando não existe */
    participacao(linha: LinhaDRP): string {
        // Antes do valor líquido não há total de referência: a célula fica vazia, e não zerada
        if (linha.participacaoFixa != null) {
            return this.percentual(this.drp?.valorTotalLiquido > 0 ? linha.participacaoFixa : 0);
        }
        return linha.campoParticipacao ? this.percentual(this.drp[linha.campoParticipacao]) : '';
    }

    /** Composição da linha sobre o valor total bruto, formatada, ou vazio quando não existe */
    composicao(linha: LinhaDRP): string {
        if (linha.composicaoFixa != null) {
            return this.percentual(linha.composicaoFixa);
        }
        return linha.campoComposicao ? this.percentual(this.drp[linha.campoComposicao]) : '';
    }

    /** Título com o prefixo da cascata, para o tooltip da linha */
    tituloCompleto(linha: LinhaDRP): string {
        return linha.numero ? `${linha.numero} ${linha.titulo}` : linha.titulo;
    }

    /** Seleciona as linhas conforme o modo atual e as permissões do usuário */
    private montarLinhas() {
        this.linhas = LINHAS_DRP.filter(linha => this.podeExibirLinha(linha)
            && (linha.nivel === 'cascata' || this.detalhado));
    }

    private podeExibirLinha(linha: LinhaDRP): boolean {
        if (linha.permissao === 'custos') {
            return this.config.canShowDRPCustos;
        }
        if (linha.permissao === 'drp') {
            return this.config.canShowDRP;
        }
        return true;
    }

    /** Um percentual ausente vira célula vazia — zero diria que o cálculo deu zero */
    private percentual(valor: number): string {
        if (valor == null) {
            return '';
        }
        return `${this.decimalFormatPipe.transform(valor, 1, 2, 2)}%`;
    }
}
